import {useEffect, useMemo, useRef, useState} from 'react';
import * as THREE from 'three';
import {useParams} from 'react-router-dom';
import useRoomPlanner from 'components/customer/RoomPlanner/lib/useRoomPlanner';
import productEvents from 'components/customer/RoomPlanner/lib/useProductEvents';
import {
    useGetLayoutsQuery,
    // usePollReportLayoutQuery,
} from 'components/customer/RoomPlanner/store/roomPlannerApi';
import {useRoomSnapshots} from 'components/customer/RoomPlannerPDF/lib/useRoomSnapshots';
import {
    Event,
    genericPubSub,
} from 'components/customer/RoomPlanner/helper/GenericPubSub';

interface RoomPlannerStandalonePreviewHook {
    /** ref to attach to the DOM element where Three.js will mount */
    mountRef: React.RefObject<HTMLDivElement>;
    /** whether all conditions are met and the snapshot-publish has been triggered */
    readyToPrint: boolean;
    /** whether the RoomPlanner itself is still generating/loading */
    isGeneratingReport: boolean;

    captureDone: boolean;
}

/**
 * Custom hook that encapsulates all of the side effects and state
 * used by <RoomPlannerStandalonePreview />.
 *
 * - Subscribes to ROOM_PLANNER_SNAPSHOT and ROOM_PLANNER_SNAPSHOT_COMPLETED events.
 * - Automatically triggers a snapshot when the scene and cabinet data are ready.
 * - Tracks the report generation and capture completion state.
 *
 * @function useRoomPlannerStandalonePreview
 * @return {RoomPlannerStandalonePreviewHook} An object containing:
 *   - mountRef: RefObject<HTMLDivElement> to attach the Three.js renderer.
 *   - readyToPrint: boolean indicating readiness to take the first snapshot.
 *   - isGeneratingReport: boolean indicating if the report is being generated.
 *   - captureDone: boolean indicating if the snapshot capture is completed.
 */
export function useRoomPlannerStandalonePreview(): RoomPlannerStandalonePreviewHook {
    // Refs to ensure we only trigger/publish once
    const hasTriggeredRef = useRef(false);
    const hasOpenedRef = useRef(false);

    // Grab jobId & roomId from the URL (react-router)
    const {roomId = '0'} = useParams<{roomId: string}>();

    // roomId is used to fetch existing layouts; jobId is used for URL generation
    const parsedRoomId = parseInt(roomId, 10);

    const [captureDone, setCaptureDone] = useState(false);

    // Suppress red overlap highlights so they don't appear in the PDF snapshots
    useEffect(() => {
        productEvents.setIsGeneratingReport(true);

        return () => {
            productEvents.setIsGeneratingReport(false);
        };
    }, []);

    // Fire off the RTK query to check if layouts are loading
    const {isLoading} = useGetLayoutsQuery(parsedRoomId);

    // Our custom hook that sets up Three.js renderer, scene, camera, etc.
    const {
        mountRef,
        renderer,
        scene,
        camera,
        isGeneratingReport,
        roomPlannerData,
        productsReady,
    } = useRoomPlanner(isLoading);

    // Hook for taking snapshots (publishing events will eventually make use of this)
    const {createSnapshots} = useRoomSnapshots();

    /**
     * Subscribe exactly once on mount to snapshot events.
     * On unmount, unsubscribe.
     */
    useEffect(() => {
        const onScreenShot = () => {
            void createSnapshots(scene, camera, renderer.current, 500);
        };

        const onScreenshotComplete = () => {
            if (!hasOpenedRef.current) {
                setCaptureDone(true);
                hasOpenedRef.current = true;
            }
        };

        genericPubSub.subscribe(
            Event.ROOM_PLANNER_SNAPSHOT_COMPLETED,
            onScreenshotComplete
        );

        return () => {
            genericPubSub.unsubscribe(
                Event.ROOM_PLANNER_SNAPSHOT,
                onScreenShot
            );
            genericPubSub.unsubscribe(
                Event.ROOM_PLANNER_SNAPSHOT_COMPLETED,
                onScreenshotComplete
            );
        };
    }, [createSnapshots, scene, camera, renderer]);

    const productLoaded = useMemo(() => {
        if (roomPlannerData && roomPlannerData.length > 0) {
            return roomPlannerData.length > 0;
        }
        return false;
    }, [roomPlannerData]);

    /**
     * Determine if we are ready to automatically trigger a snapshot:
     * - We have not triggered yet
     * - cabinetGroupList is a non-empty array
     * - camera & scene exist
     */
    const readyToPrint = useMemo(
        () =>
            !hasTriggeredRef.current &&
            Boolean(camera) &&
            Boolean(scene) &&
            productLoaded,
        [camera, scene, productLoaded, hasTriggeredRef]
    );

    /**
     * As soon as readyToPrint becomes true, publish the snapshot event once.
     */
    useEffect(() => {
        if (readyToPrint && productsReady) {
            hasTriggeredRef.current = true;
            void genericPubSub.publish(Event.ROOM_PLANNER_SNAPSHOT, {
                url: ``,
            });
        }
    }, [readyToPrint, productsReady]);

    useEffect(() => {
        // This effect ensures we cleanup the threejs stuffs after rendering the pdf
        // This will prevent memory leaks
        if (captureDone) {
            type Disposable =
                | THREE.BufferGeometry
                | THREE.Material
                | THREE.Texture;
            const disposables: Disposable[] = [];
            scene.traverse((object: THREE.Object3D) => {
                // Check if the object is a valid Three.js object before accessing its properties
                if (
                    object instanceof THREE.Mesh ||
                    object instanceof THREE.Line ||
                    object instanceof THREE.Points
                ) {
                    if (object.geometry instanceof THREE.BufferGeometry) {
                        disposables.push(object.geometry);
                    }
                    if (object.material) {
                        const materials = Array.isArray(object.material)
                            ? (object.material as THREE.Material[])
                            : [object.material as THREE.Material];

                        for (const material of materials) {
                            disposables.push(material);

                            for (const key of Object.keys(material)) {
                                const value =
                                    material[key as keyof THREE.Material];
                                if (value && value instanceof THREE.Texture) {
                                    disposables.push(value);
                                }
                            }
                        }
                    }
                }
            });

            for (let i = scene.children.length - 1; i >= 0; i--) {
                // eslint-disable-next-line security/detect-object-injection -- this is fine as we are just accessing by index
                scene.remove(scene.children[i]);
            }

            for (const resource of disposables) {
                resource.dispose();
            }
            renderer.current.setRenderTarget(null);
            renderer.current.renderLists.dispose();
            renderer.current.dispose();
        }
    }, [captureDone, renderer, scene]);

    return {
        mountRef,
        readyToPrint,
        isGeneratingReport,
        captureDone,
    };
}
